import { memo, useCallback, useEffect, useRef, useState } from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import { NotesNodeData } from '../../types';
import "@reactflow/node-resizer/dist/style.css";

function NotesNode({
  data,
  selected,
  id,
}: NodeProps<NotesNodeData>) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { getNode } = useReactFlow();
  
  // Get the current node to access its width and height
  const node = getNode(id);
  
  // Initialize with node's width/height if available, otherwise use defaults
  const [nodeSize, setNodeSize] = useState({ 
    width: (node?.width && node.width > 0) ? node.width : 160, 
    height: (node?.height && node.height > 0) ? node.height : 80 
  });
  const currentSizeRef = useRef(nodeSize);
  const previousHeightRef = useRef(nodeSize.height);
  const { setNodes } = useReactFlow();
  const isManuallyResized = useRef(true); // Set to true by default to prevent auto-resizing on initial render

  // Keep the ref updated with the latest state
  useEffect(() => {
    currentSizeRef.current = nodeSize;
  }, [nodeSize]);

  // Get the appropriate background color class based on the data
  const getBackgroundColorClass = useCallback(() => {
    if (!data.color) return 'bg-yellow-100 dark:bg-yellow-900/20';

    // Map color names to Tailwind classes
    const colorMap: Record<string, string> = {
      'yellow': 'bg-yellow-100 dark:bg-yellow-900/20',
      'blue': 'bg-blue-100 dark:bg-blue-900/20',
      'green': 'bg-green-100 dark:bg-green-900/20',
      'red': 'bg-red-100 dark:bg-red-900/20',
      'purple': 'bg-purple-100 dark:bg-purple-900/20',
    };

    return colorMap[data.color] || 'bg-yellow-100 dark:bg-yellow-900/20';
  }, [data.color]);

  // Get the appropriate border color class based on the data
  const getBorderColorClass = useCallback(() => {
    if (!data.color) return 'border-yellow-200 dark:border-yellow-800';

    // Map color names to Tailwind classes
    const colorMap: Record<string, string> = {
      'yellow': 'border-yellow-200 dark:border-yellow-800',
      'blue': 'border-blue-200 dark:border-blue-800',
      'green': 'border-green-200 dark:border-green-800',
      'red': 'border-red-200 dark:border-red-800',
      'purple': 'border-purple-200 dark:border-purple-800',
    };

    return colorMap[data.color] || 'border-yellow-200 dark:border-yellow-800';
  }, [data.color]);

  // Handle manual resizing and update the node's dimensions in the flow
  const onResize = useCallback((_: any, newSize: { width: number; height: number }) => {
    isManuallyResized.current = true;
    setNodeSize(newSize);
    
    // Update the node's dimensions in the flow
    setNodes(nodes => 
      nodes.map(node => {
        if (node.id === id) {
          return {
            ...node,
            width: newSize.width,
            height: newSize.height
          };
        }
        return node;
      })
    );
  }, [id, setNodes]);

  // Calculate node size immediately when content changes
  const calculateNodeSizeNow = useCallback(() => {
    // Skip auto-sizing if the node has been manually resized
    if (isManuallyResized.current) {
      return;
    }
    
    if (nodeRef.current) {
      const contentElement = nodeRef.current.querySelector('.node-content');
      if (contentElement) {
        // Get content dimensions
        const contentWidth = contentElement.getBoundingClientRect().width;
        const contentHeight = contentElement.getBoundingClientRect().height;
        
        // Add padding (similar to what was in nodeUtils)
        const contentWidthWithPadding = contentWidth + 40; // 32px horizontal padding + buffer
        const contentHeightWithPadding = contentHeight + 12; // 12px vertical padding
        
        // Define min/max constraints
        const MIN_WIDTH = 160;
        const MAX_WIDTH = 500;
        const MIN_HEIGHT = 80;
        const MAX_HEIGHT = 500;
        const GRID_SIZE = 8;
        
        // Apply min/max constraints
        let adjustedWidth = Math.max(MIN_WIDTH, Math.min(contentWidthWithPadding, MAX_WIDTH));
        let adjustedHeight = Math.max(MIN_HEIGHT, Math.min(contentHeightWithPadding, MAX_HEIGHT));
        
        // Snap to grid
        adjustedWidth = Math.ceil(adjustedWidth / GRID_SIZE) * GRID_SIZE;
        adjustedHeight = Math.round(adjustedHeight / GRID_SIZE) * GRID_SIZE;
        
        // Create new size object
        const newSize = { width: adjustedWidth, height: adjustedHeight };
        
        // Only update if size actually changed
        if (newSize.width !== currentSizeRef.current.width || newSize.height !== currentSizeRef.current.height) {
          // Calculate height difference for center-aligned resizing
          const heightDiff = newSize.height - previousHeightRef.current;
          
          // Store the new height as previous for next resize
          previousHeightRef.current = newSize.height;
          
          // Update the node size
          setNodeSize(newSize);
          
          // Adjust node position for center-aligned resizing if height changed
          if (heightDiff !== 0) {
            // Move the node up by half the height difference to create center-aligned resize effect
            setNodes(nodes => 
              nodes.map(node => {
                if (node.id === id) {
                  return {
                    ...node,
                    position: {
                      x: node.position.x,
                      y: node.position.y - (heightDiff / 2)
                    }
                  };
                }
                return node;
              })
            );
          }
        }
      }
    }
  }, [data, id, setNodes]);

  // Initial size calculation on mount
  useEffect(() => {
    // Calculate size immediately on first render
    calculateNodeSizeNow();
    
    // We don't need the MutationObserver since we're using manual resizing
  }, []);

  // Recalculate when data or selection changes
  useEffect(() => {
    calculateNodeSizeNow();
  }, [data, selected, calculateNodeSizeNow]);

  return (
    <div
      ref={nodeRef}
      style={{ width: nodeSize.width, height: nodeSize.height }}
      className={`
        inline-block pl-4 pr-4 py-3 rounded-lg border transition-all cursor-pointer shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark
        ${getBackgroundColorClass()} ${getBorderColorClass()}
        ${selected 
          ? "" 
          : ""
        }
      `}
    >
      {selected && (
        <NodeResizer
          minWidth={160}
          minHeight={80}
          maxWidth={500}
          maxHeight={500}
          isVisible={selected}
          onResize={onResize}
          handleStyle={{ 
            width: '8px', 
            height: '8px',
            backgroundColor: '#338cf1',
            borderRadius: '50%'
          }}
          lineStyle={{ 
            borderWidth: '1px', 
            borderColor: '#338cf1',
            borderStyle: 'solid'
          }}
        />
      )}
      
      <div className="flex flex-col gap-2 node-content w-full h-full">
        {/* Name - only show if visibility is not hidden */}
        {data.name && data.nameVisibility !== "hidden" && (
          <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate w-full">
            {data.name}
          </h3>
        )}
        
        {/* Notes - only show if visibility is not hidden and we have notes */}
        {data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
          <div 
            className="text-[10px] text-gray-600 dark:text-gray-300 overflow-y-auto overflow-x-hidden pr-1 break-words flex-grow"
            style={{ maxHeight: 'calc(100% - 24px)' }} // Subtract space for name if present
          >
            {data.notes?.split('\n').map((line, index, array) => (
              <span key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(NotesNode);
